<div class="container-fluid">
  <app-page-header
    icon="fe-user-check"
    pretitle="Configurações"
    title="Utilizadores"
    description="Convide pessoas para a equipa e defina o papel de cada uma (administrador, gestor ou observador)."
  >
    <div actions>
      <button class="btn btn-white" (click)="openInvite()">
        <i class="fe fe-user-plus me-1"></i> Convidar utilizador
      </button>
    </div>
  </app-page-header>
</div>

<div class="container-fluid">
  <!-- Link de convite gerado (envio de e-mail é stub: o administrador partilha o link) -->
  @if (inviteResult(); as result) {
    <div class="alert alert-success d-flex align-items-start" role="alert">
      <i class="fe fe-check-circle mt-1 me-2"></i>
      <div class="flex-grow-1">
        <div class="fw-bold mb-1">Convite criado para {{ result.user.name }}.</div>
        <div class="small mb-2">Partilhe este link para a pessoa definir a palavra-passe e ativar o acesso:</div>
        <div class="input-group input-group-sm">
          <input type="text" class="form-control" [value]="result.inviteUrl" readonly />
          <button class="btn btn-white" (click)="copyLink(result.inviteUrl)">
            <i class="fe fe-copy me-1"></i> {{ linkCopied() ? 'Copiado' : 'Copiar' }}
          </button>
        </div>
      </div>
    </div>
  }

  <!-- Formulário de convite -->
  @if (showInvite()) {
    <div class="card">
      <div class="card-header">
        <div class="d-flex align-items-center">
          <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-primary-soft text-primary"><i class="fe fe-user-plus"></i></span></span>
          <h4 class="card-header-title mb-0">Convidar utilizador</h4>
        </div>
      </div>
      <div class="card-body">
        <form [formGroup]="inviteForm" (ngSubmit)="submitInvite()">
          <div class="row g-3">
            <div class="col-12 col-md-4">
              <label class="form-label">Nome</label>
              <input type="text" class="form-control" formControlName="name" placeholder="Nome completo" />
            </div>
            <div class="col-12 col-md-4">
              <label class="form-label">E-mail</label>
              <input type="email" class="form-control" formControlName="email" placeholder="pessoa@empresa.com" />
            </div>
            <div class="col-12 col-md-4">
              <label class="form-label">Papel</label>
              <select class="form-select" formControlName="role">
                @for (option of roleOptions; track option.value) { <option [value]="option.value">{{ option.label }}</option> }
              </select>
            </div>
          </div>
          @if (inviteError()) { <p class="text-danger small mt-3 mb-0">{{ inviteError() }}</p> }
          <div class="mt-3 d-flex gap-2">
            <button type="submit" class="btn btn-primary" [disabled]="inviteForm.invalid">
              <i class="fe fe-send me-1"></i> Enviar convite
            </button>
            <button type="button" class="btn btn-white" (click)="showInvite.set(false)">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  }

  @if (actionError()) { <div class="alert alert-danger">{{ actionError() }}</div> }

  <div class="card">
    <div class="card-body p-0">
      @if (error()) { <p class="text-danger text-center py-5 mb-0">{{ error() }}</p> }
      @else if (loading()) { <p class="text-muted text-center py-5 mb-0">A carregar…</p> }
      @else if (users().length === 0) { <p class="text-muted text-center py-5 mb-0">Nenhum utilizador.</p> }
      @else {
        <div class="list-group list-group-flush">
          @for (user of users(); track user.id) {
            <div class="list-group-item">
              <div class="row align-items-center">
                <div class="col-auto">
                  <span class="avatar avatar-sm">
                    <span class="avatar-title rounded-circle bg-primary-soft text-primary">{{ initial(user.name) }}</span>
                  </span>
                </div>
                <div class="col ms-n2">
                  <h4 class="mb-1">
                    {{ user.name }}
                    @if (isSelf(user)) { <span class="badge bg-secondary-soft ms-1">você</span> }
                  </h4>
                  <p class="card-text small text-muted mb-0">{{ user.email }}</p>
                </div>
                <div class="col-auto">
                  <!-- "Ativo" sem palavra-passe definida é acesso que não existe: mostra-se como pendente. -->
                  @if (!user.passwordSet && user.status !== 'DISABLED') {
                    <span class="badge bg-warning-soft" title="O convite ainda não foi aceite — este acesso não entra.">
                      <i class="fe fe-clock me-1"></i>Acesso por ativar
                    </span>
                  } @else {
                    <span class="badge bg-{{ statusTone(user.status) }}-soft">{{ statusLabels[user.status] }}</span>
                  }
                </div>
                <div class="col-auto">
                  <select class="form-select form-select-sm" style="width: auto"
                          (change)="changeRole(user, $event)" [disabled]="isSelf(user)">
                    @for (option of roleOptions; track option.value) {
                      <option [value]="option.value" [selected]="option.value === user.role">{{ option.label }}</option>
                    }
                  </select>
                </div>
                <div class="col-auto">
                  <button class="btn btn-sm btn-white" (click)="openModules(user)"
                          [class.btn-primary]="openModulesFor()?.id === user.id"
                          title="Definir a que módulos este utilizador chega">
                    <i class="fe fe-grid me-1"></i> Módulos
                  </button>
                </div>
                <!-- A linha tem seis colunas e deixou de caber com o terceiro botão: abaixo do
                     ecrã largo, as ações passam a ocupar uma linha própria, encostadas à direita. -->
                <div class="col-12 col-xl-auto d-flex gap-2 justify-content-end flex-wrap mt-2 mt-xl-0">
                  @if (user.status === 'INVITED' || (!user.passwordSet && user.status !== 'DISABLED')) {
                    <button class="btn btn-sm btn-white text-nowrap" (click)="resend(user)"
                            title="Gera um novo link de convite">
                      <i class="fe fe-send me-1"></i> Reenviar convite
                    </button>
                  } @else {
                    <!-- Quem se esquece da palavra-passe não tem como voltar sozinho: é por aqui
                         que o administrador lhe dá um link novo. A senha antiga deixa de servir. -->
                    @if (user.status === 'ACTIVE') {
                      <button class="btn btn-sm btn-white text-nowrap" (click)="askReset(user)"
                              title="Gera um link para a pessoa definir uma palavra-passe nova">
                        <i class="fe fe-key me-1"></i> Repor acesso
                      </button>
                    }
                    <button class="btn btn-sm btn-white text-nowrap" [disabled]="isSelf(user)"
                            (click)="toggleStatus(user)">
                      {{ user.status === 'ACTIVE' ? 'Desativar' : 'Ativar' }}
                    </button>
                  }
                </div>
              </div>

              @if (openModulesFor()?.id === user.id) {
                <!-- Cada módulo é um âmbito de ocorrências: aqui decide-se a que chega, com que
                     nível e entre que datas se vêem os registos. -->
                <div class="border-top mt-3 pt-3">
                  @if (modulesLoading()) {
                    <p class="text-muted small mb-0">A carregar módulos…</p>
                  } @else {
                    <div class="table-responsive">
                      <table class="table table-sm table-nowrap mb-0">
                        <thead>
                          <tr>
                            <th>Módulo</th>
                            <th class="text-center">Ver</th>
                            <th class="text-center">Editar</th>
                            <th class="text-center">Criar</th>
                            <th class="text-center">Extrair relatórios</th>
                            <th>Registos desde</th>
                            <th>Registos até</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (module of modules(); track module.scope) {
                            <tr>
                              <td class="fw-bold">{{ scopeLabels[module.scope] }}</td>
                              <td class="text-center">
                                <input class="form-check-input" type="checkbox" [checked]="module.canView"
                                       (change)="toggleModule(module, 'canView', $any($event.target).checked)" />
                              </td>
                              <td class="text-center">
                                <input class="form-check-input" type="checkbox" [checked]="module.canEdit"
                                       (change)="toggleModule(module, 'canEdit', $any($event.target).checked)" />
                              </td>
                              <td class="text-center">
                                <input class="form-check-input" type="checkbox" [checked]="module.canCreate"
                                       (change)="toggleModule(module, 'canCreate', $any($event.target).checked)" />
                              </td>
                              <td class="text-center">
                                <input class="form-check-input" type="checkbox" [checked]="module.canExport"
                                       (change)="toggleModule(module, 'canExport', $any($event.target).checked)" />
                              </td>
                              <td style="min-width: 10rem">
                                <app-date-picker [(ngModel)]="module.accessFrom" [disabled]="!module.canView" />
                              </td>
                              <td style="min-width: 10rem">
                                <app-date-picker
                                  [(ngModel)]="module.accessTo"
                                  [min]="module.accessFrom"
                                  [disabled]="!module.canView"
                                />
                              </td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                    <p class="text-muted small mt-2 mb-0">
                      Sem datas, o módulo não tem limite. Com datas, só se vêem as ocorrências cuja data
                      cai dentro da janela.
                    </p>
                    @if (modulesError()) { <div class="alert alert-danger small py-2 mt-2 mb-0">{{ modulesError() }}</div> }
                    <div class="d-flex justify-content-end gap-2 mt-3">
                      <button class="btn btn-sm btn-white" (click)="closeModules()">Cancelar</button>
                      <button class="btn btn-sm btn-primary" (click)="saveModules()" [disabled]="modulesSaving()">
                        {{ modulesSaving() ? 'A guardar…' : 'Guardar módulos' }}
                      </button>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  </div>
</div>

@if (pendingReset(); as alvo) {
  <app-confirm-dialog
    title="Repor o acesso?"
    [message]="'A palavra-passe de ' + alvo.name + ' deixa de funcionar. Vai receber um link para definir uma nova — copie-o e entregue-lho.'"
    confirmLabel="Repor acesso"
    cancelLabel="Cancelar"
    [danger]="true"
    (confirm)="confirmReset()"
    (cancel)="cancelReset()"
  />
}
